import React from 'react';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import DefaultButton from '../../../../../../global/default-button/default-button';
import { DragDropList } from '../../../../../../global/drag-drop-list/drag-drop-list';
import CollapseSection from '../../../../../../global/collapse-section/collapse-section';
import { EDIT_TEXT, LOADING } from '../../../../../../../constants/common-labels.constants';
import {
    HUB_MENU_NO_LINKS_TEXT,
    HUB_MENU_TEXT,
} from '../../../../../event-hubs.text';
import { HubMenuSettingsVM } from './hub-menu-settings.viewmodel';
import { HubMenuLinkModal } from './hub-menu-link-modal';
import { MenuItemTypePicker, MenuItemType } from './menu-item-type-picker';
import { MENU_ITEM_TYPE_DISPLAY_NAMES } from './menu-item-type-picker.config';
import { IHubPageDTO, IHubMenuLinkDTO } from '../../../../../../../api/event-hubs.dao';
import './hub-menu-settings.scss';

type HubMenuSettingsProps = {
    vm: HubMenuSettingsVM;
};

type MenuItem = {
    id: number;
    type: MenuItemType;
    page?: IHubPageDTO;
    link?: IHubMenuLinkDTO;
};

const isLink = (item: IHubPageDTO | IHubMenuLinkDTO): item is IHubMenuLinkDTO => {
    return 'externalLink' in item && item.externalLink != null;
};

export const HubMenuSettings: React.FC<HubMenuSettingsProps> = observer(({ vm }) => {
    if (vm.isLoading()) {
        return (
            <div className='hub-menu-settings__loading'>
                <FormattedMessage {...LOADING} values={{ suffix: '...' }} />
            </div>
        );
    }

    const allMenuItems = vm.getMenuItems();
    const menuItems: MenuItem[] = allMenuItems.map(item =>
        isLink(item)
            ? { id: item.hubMenuLinkId, type: MenuItemType.LINK, link: item }
            : { id: item.hubPageId, type: MenuItemType.CUSTOM, page: item }
    );

    const activeMenuItems = menuItems.filter(item => {
        if (item.type !== MenuItemType.LINK) {
            return true;
        }
        return item.link?.enabled !== false;
    });

    const activeItemIds = new Set(activeMenuItems.map(item =>
        item.type === MenuItemType.LINK ? item.link?.hubMenuLinkId : item.page?.hubPageId
    ));

    const disabledMenuItems = menuItems.filter(item =>
        item.type === MenuItemType.LINK && item.link?.enabled === false && !activeItemIds.has(item.link?.hubMenuLinkId)
    );

    const renderMenuItem = (menuItem: MenuItem): JSX.Element => {
        const menuItemIsLink = menuItem.type === MenuItemType.LINK;
        const pageType = MENU_ITEM_TYPE_DISPLAY_NAMES[menuItem.type];
        const fullLabel = menuItemIsLink ? menuItem.link.label : (menuItem.page?.displayName || menuItem.page?.pageName);
        const name = menuItemIsLink && fullLabel?.includes(':')
            ? fullLabel.split(':')[1].trim()
            : fullLabel;

        return (
            <div className={`hub-menu-settings__row hub-menu-settings__row--${menuItem.type.toLowerCase()}`}>
                <span className='hub-menu-settings__row-label'>
                    <strong>{pageType}</strong>:{' '}{name}
                </span>
                {menuItemIsLink && (
                    <div className='hub-menu-settings__row-actions'>
                        <DefaultButton
                            modifier='slim'
                            className='hub-menu-settings__edit-button'
                            text={<FormattedMessage {...EDIT_TEXT} />}
                            onClick={() => vm.openEditLinkModal(menuItem.link)}
                        />
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className='hub-menu-settings'>
            <div className='hub-menu-settings__title'>
                <FormattedMessage {...HUB_MENU_TEXT} />
            </div>
            <div className='hub-menu-settings__list'>
                {activeMenuItems.length > 0 ? (
                    <DragDropList
                        dragHandleBy='item'
                        showDropPlaceholder
                        data={activeMenuItems}
                        renderItem={renderMenuItem}
                        onDragEnd={reordered => {
                            const items = reordered.map(item => item.type === MenuItemType.CUSTOM ? item.page : item.link);
                            vm.reorderMenuItems(items);
                        }}
                    />
                ) : (
                    <div className='hub-menu-settings__empty'>
                        <FormattedMessage {...HUB_MENU_NO_LINKS_TEXT} />
                    </div>
                )}
            </div>

            <MenuItemTypePicker
                isOpen={vm.isTypePickerOpen()}
                onOpen={vm.openTypePicker}
                onClose={vm.closeTypePicker}
                onSelect={(type: MenuItemType) => {
                    if (type === MenuItemType.LINK) {
                        vm.onMenuItemTypeSelected();
                    }
                }}
                disabled={vm.isSaving()}
                className='hub-menu-settings__add-button'
            />

            <div className='hub-menu-settings__disabled-links-section'>
                <CollapseSection title='Disabled Links' isDefaultCollapsed>
                    {disabledMenuItems.length > 0 && (
                        <div className='hub-menu-settings__disabled-links-list'>
                            {disabledMenuItems.map(renderMenuItem)}
                        </div>
                    )}
                </CollapseSection>
            </div>

            <HubMenuLinkModal vm={vm} />
        </div>
    );
});
